import { useMemo } from "react";
import { supabase } from "../lib/supabase";
import type { SourceHealthRow } from "../lib/supabase";
import { useSupabaseQuery } from "../hooks/useSupabaseQuery";
import { fmtRelative, fmtInt } from "../lib/format";

export default function SourceHealth() {
  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }, []);

  const { data, error, loading } = useSupabaseQuery<SourceHealthRow[]>(
    async () =>
      supabase
        .from("source_health")
        .select("*")
        .gte("checked_at", since)
        .order("checked_at", { ascending: false })
        .limit(2000) as unknown as Promise<any>,
    [since],
    60_000,
  );

  const bySource = useMemo(() => {
    const rows = data || [];
    const map = new Map<string, {
      source: string;
      errors: number;
      latest: string | null;
      latestMessage: string | null;
    }>();
    for (const r of rows) {
      const src = r.source_name || "unknown";
      let entry = map.get(src);
      if (!entry) {
        entry = { source: src, errors: 0, latest: null, latestMessage: null };
        map.set(src, entry);
      }
      entry.errors++;
      if (!entry.latest || (r.checked_at || "") > entry.latest) {
        entry.latest = r.checked_at;
        entry.latestMessage = r.error_message;
      }
    }
    return [...map.values()].sort((a, b) => b.errors - a.errors);
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Failing sources (7d)" value={fmtInt(bySource.length)} />
        <Stat label="Total errors (7d)" value={fmtInt(bySource.reduce((s, x) => s + x.errors, 0))} />
        <Stat
          label="Most recent error"
          value={bySource[0] ? fmtRelative(bySource[0].latest) : "—"}
          sub={bySource[0]?.source}
        />
      </div>

      <div className="card overflow-x-auto">
        <h3 className="text-sm font-medium mb-3">Sources with errors (7d)</h3>
        {loading && !data && <p className="text-sm text-chrome-400">Loading…</p>}
        {error && <p className="text-sm text-bad">{(error as Error).message}</p>}
        {data && bySource.length === 0 && (
          <p className="text-sm text-ok">No source errors in the last 7 days.</p>
        )}
        {bySource.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-chrome-400 uppercase">
              <tr>
                <th className="text-left font-normal pb-2">Source</th>
                <th className="text-right font-normal pb-2">Errors</th>
                <th className="text-left font-normal pb-2 pl-4">Last seen</th>
                <th className="text-left font-normal pb-2 pl-4">Latest message</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {bySource.map((s) => (
                <tr key={s.source} className="border-t border-ink-800 align-top">
                  <td className="py-2 pr-4">{s.source}</td>
                  <td className="py-2 text-right">{fmtInt(s.errors)}</td>
                  <td className="py-2 pl-4 text-chrome-300 whitespace-nowrap">{fmtRelative(s.latest)}</td>
                  <td className="py-2 pl-4 text-chrome-400 max-w-xl truncate">
                    {s.latestMessage || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-xs text-chrome-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold mt-1 font-mono">{value}</p>
      {sub && <p className="text-xs text-chrome-400 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}
